import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Modal } from '@/components/ui/modal';
import { DEV_EUI_REGEX, JOIN_EUI_REGEX, APP_KEY_REGEX } from '@/lib/validation-constants';
import type {
  ProvisionedMeter,
  CreateProvisionedMeterRequest,
  UpdateProvisionedMeterRequest,
} from '@/types/api';

interface ProvisionedMeterFormModalProps {
  meter: ProvisionedMeter | null;
  onClose: () => void;
  onSave: (data: CreateProvisionedMeterRequest | UpdateProvisionedMeterRequest) => Promise<void>;
}

interface FormErrors {
  serialNumber?: string;
  devEui?: string;
  joinEui?: string;
  appKey?: string;
}

export function ProvisionedMeterFormModal({ meter, onClose, onSave }: ProvisionedMeterFormModalProps) {
  const [formData, setFormData] = useState({
    serialNumber: meter?.serialNumber ?? '',
    devEui: meter?.devEui ?? '',
    joinEui: meter?.joinEui ?? '',
    appKey: meter?.appKey ?? '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  function validate(): boolean {
    const next: FormErrors = {};

    if (!formData.serialNumber.trim()) {
      next.serialNumber = 'Serial number is required.';
    } else if (formData.serialNumber.length > 20) {
      next.serialNumber = 'Serial number must be 20 characters or fewer.';
    }

    if (!DEV_EUI_REGEX.test(formData.devEui)) {
      next.devEui = 'DevEUI must be exactly 16 hexadecimal characters.';
    }

    if (!JOIN_EUI_REGEX.test(formData.joinEui)) {
      next.joinEui = 'JoinEUI must be exactly 16 hexadecimal characters.';
    }

    if (!APP_KEY_REGEX.test(formData.appKey)) {
      next.appKey = 'AppKey must be exactly 32 hexadecimal characters.';
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleFieldChange(field: keyof typeof formData, value: string) {
    const uppercased = field === 'serialNumber' ? value : value.toUpperCase();
    setFormData(prev => ({ ...prev, [field]: uppercased }));
    // Clear field error on change
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      await onSave(formData);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={meter ? 'Edit Provisioned Meter' : 'Add Provisioned Meter'}
      description="Enter the meter credentials below."
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Serial Number"
          required
          value={formData.serialNumber}
          onChange={(e) => handleFieldChange('serialNumber', e.target.value)}
          placeholder="e.g., SN-00012345"
          maxLength={20}
          error={errors.serialNumber}
        />

        <Input
          label="DevEUI"
          required
          value={formData.devEui}
          onChange={(e) => handleFieldChange('devEui', e.target.value)}
          placeholder="e.g., 0123456789ABCDEF"
          maxLength={16}
          error={errors.devEui}
          helperText="16-character hexadecimal identifier"
          className="font-mono"
        />

        <Input
          label="JoinEUI"
          required
          value={formData.joinEui}
          onChange={(e) => handleFieldChange('joinEui', e.target.value)}
          placeholder="e.g., 0123456789ABCDEF"
          maxLength={16}
          error={errors.joinEui}
          helperText="16-character hexadecimal identifier"
          className="font-mono"
        />

        <Input
          label="AppKey"
          required
          value={formData.appKey}
          onChange={(e) => handleFieldChange('appKey', e.target.value)}
          placeholder="e.g., 0123456789ABCDEF0123456789ABCDEF"
          maxLength={32}
          error={errors.appKey}
          helperText="32-character hexadecimal key"
          className="font-mono"
        />

        <div className="flex gap-3 pt-4">
          <Button type="button" variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button type="submit" isLoading={isSubmitting} className="flex-1">
            {meter ? 'Update' : 'Add'} Meter
          </Button>
        </div>
      </form>
    </Modal>
  );
}
